// Styles
import "./UploadModal.scss";

// UI Components
import { Headline3 } from "@bms-ai/bms-ai-ui-lib/components";

// Icons
import CheckCircleIcon from "../../_assets/icons/CheckCircleIcon";
import ErrorCircleIcon from "../../_assets/icons/ErrorCircleIcon";
import DeleteIcon from "../../_assets/icons/DeleteIcon";
import UploadingIcon from "../../_assets/icons/UploadingIcon";

// Types
interface PendingUpload {
  file: File; // Sanitized file for API upload
  originalFile?: File; // Original file for display purposes
  uploadType: string;
  progress: number;
  status: "uploading" | "complete" | "error";
  error?: string;
  response?: any;
}

interface UploadModalProps {
  isOpen: boolean;
  uploads: PendingUpload[];
  onCancel: () => void;
  onConfirm: () => void;
  onRemove?: (index: number) => void;
  onAddDocument?: () => void;
}

const UploadModal: React.FC<UploadModalProps> = ({
  isOpen,
  uploads,
  onCancel,
  onConfirm,
  onRemove,
  onAddDocument,
}) => {
  if (!isOpen) return null;

  const hasUploadingFiles = uploads.some((u) => u.status === "uploading");

  return (
    <div className="upload-modal-overlay">
      <div className="upload-modal" onClick={(e) => e.stopPropagation()}>
        <div className="upload-modal-header">
          <Headline3>Documents</Headline3>
        </div>
        <div className="upload-modal-content">
          {uploads.map((upload, index) => (
            <div key={index} className="upload-item-row">
              <div className="upload-item-info">
                {upload.status === "complete" ? (
                  <CheckCircleIcon className="file-icon success-icon" />
                ) : upload.status === "error" ? (
                  <ErrorCircleIcon className="file-icon error-icon" />
                ) : upload.status === "uploading" ? (
                  <UploadingIcon className="file-icon uploading-icon" />
                ) : (
                  <></>
                )}
                <div className="upload-item-details">
                  <div className="upload-item-name">{upload.originalFile?.name || upload.file.name}</div>
                  {upload.status === "uploading" && (
                    <div className="upload-item-status">
                      <div className="upload-progress-bar">
                        <div
                          className="upload-progress-fill"
                          style={{ width: `${upload.progress}%` }}
                        />
                      </div>
                      <span className="upload-progress-percent">
                        {upload.progress}%
                      </span>
                    </div>
                  )}
                  {upload.status === "complete" && (
                    <div className="upload-item-status-text success">
                      Upload complete
                    </div>
                  )}
                  {upload.status === "error" && (
                    <div className="upload-item-status-text error">
                      {upload.error || "There was an error"}
                    </div>
                  )}
                </div>
              </div>
              <div 
                className="upload-item-icon"
                onClick={() => onRemove?.(index)}
                style={{ cursor: 'pointer' }}
              >
                <DeleteIcon className="delete-icon" />
              </div>
            </div>
          ))}

          <div className="add-document-btn-wrapper">
            <button 
              className="add-document-btn"
              onClick={onAddDocument}
              disabled={hasUploadingFiles}
            >
              <span>Add document</span>
            </button>
          </div>
        </div>
        <div className="upload-modal-footer">
          <button className="cancel-btn" onClick={onCancel}>
            Cancel
          </button>
          <button
            className="confirm-btn"
            onClick={onConfirm}
            disabled={hasUploadingFiles}
          >
            Confirm
          </button>
        </div>
      </div>
    </div>
  );
};

export default UploadModal;
